import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { SELECTED_STORE_COOKIE_NAME } from 'constants/cookies';
import { useEffectOnce, useEngagementGoals, useRequiredContext } from 'hooks';
import type { StoreId } from 'types';
import { getCookie, setCookie } from 'utils/cookies';
import { parseJson } from 'utils/string';

export interface StoreIdName {
	id: StoreId;
	name: string;
}

interface SelectedStoreContextType {
	removeSelectedStore: () => void;
	selectedStore: StoreIdName | undefined;
	updateSelectedStore: (store: StoreIdName | undefined) => void;
}
export const SelectedStoreContext = createContext<
	SelectedStoreContextType | undefined
>(undefined);

interface Props {
	children: ReactNode;
}

export function SelectedStoreContextProvider({ children }: Props) {
	const [selectedStore, setSelectedStore] = useState<StoreIdName | undefined>();

	useEffectOnce(() =>
		setSelectedStore(
			parseJson(getCookie(SELECTED_STORE_COOKIE_NAME)) ?? undefined,
		),
	);

	const { sendEngagement } = useEngagementGoals();

	const updateSelectedStore = useCallback(
		(storeInput: StoreIdName | undefined) => {
			if (!storeInput) {
				return;
			}
			// Repack to handle duck typing where objects with additional
			// unneeded properties end up here via updateSelectedStore
			const store = { id: storeInput.id, name: storeInput.name };

			setSelectedStore(store);
			setCookie({
				name: SELECTED_STORE_COOKIE_NAME,
				value: JSON.stringify(store),
				maxAge: '1-year',
			});
			sendEngagement({
				type: 'chooseHomeStore',
				data: {
					storeId: store.id,
				},
			});
		},
		[sendEngagement],
	);

	const removeSelectedStore = useCallback(() => {
		setSelectedStore(undefined);
	}, []);

	const value = useMemo(
		() => ({
			selectedStore,
			updateSelectedStore,
			removeSelectedStore,
		}),
		[updateSelectedStore, selectedStore, removeSelectedStore],
	);

	return <SelectedStoreContext value={value}>{children}</SelectedStoreContext>;
}
SelectedStoreContextProvider.displayName = 'SelectedStoreContextProvider';

export function useSelectedStore() {
	return useRequiredContext(SelectedStoreContext, 'SelectedStoreContext');
}
